import React, { useMemo, useState, useEffect } from 'react';
import {
  ArrowLeft,
  Mail,
  Phone,
  CalendarClock,
  Layers,
  Bell,
  History,
  LayoutDashboard,
  AlertTriangle,
  FileSignature,
  X,
  Eye,
  Clock,
  Lock,
  Ban,
  RefreshCw,
  Wallet,
  CreditCard,
  Banknote,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { PageSearchBar } from '../../components/common/PageSearchBar';
import { StatsCard } from '../../components/common/StatsCard';
import { MobileModalPortal } from '../../components/common/MobileModalPortal';
import { ExportMenu, type ExportRow } from '../../lib/exportReports';
import { AmountInput } from '../../components/common/AmountInput';
import { formatInrPaise, parseAmountInput } from '../../lib/formatInr';
import { computeOutPaymentDue } from '../../lib/financeMath';
import { exportLoanApplicationPdf } from '../../lib/loanApplicationPdf';
import { ApiError, smsApi } from '../../api';
import { readFinanceRecords, readHandLoanRecords } from '../../lib/workspaceStore';
import { useScrollToTop } from '../../lib/useScrollToTop';
import { useCanApproveWaivers, useCanWrite } from '../../lib/permissions';
import { planAllowsItem } from '../../lib/subscription';
import { SMS_REMINDER_FEATURE_ID } from '../../lib/planEntitlements';
import { PaymentViaFields, validatePaymentVia, emptyPaymentVia, paymentViaPersistFields } from '../../components/common/PaymentViaFields';
import { readCompanyBanks } from '../../lib/financeCatalogs';
import { resolveBankLabel, paymentViaDisplayLabel } from '../../lib/paymentVia';
import { parseHandLoanInterestType, replayHandLoanBalances, handLoanLedgerAsOfMs } from '../../lib/handLoanBalances';
import { adminEntryIso, defaultAdminEntryDate, resolveWorkspaceAsOfDate, resolveWorkspaceAsOfMs } from '../../lib/workspaceAsOf';
import { useTranslation } from '../../lib/i18n';
import { translateStatus } from '../../lib/i18n/helpers';
import { queueRefinance } from '../../lib/financeRefinance';
import {
  computeCustomerOverdueAnalysis,
  computeFacilityUnpaidOverdue,
  formatDisplayDate,
  readOverduePenaltyConfig,
  type OverdueInstallmentRow,
} from '../../lib/overdue';
import { daysUntilDue } from '../../lib/smsReminders';
import {
  getInstallmentScheduleForCustomer,
  getFacilityCollectionSnapshot,
  facilityHasActionableCollectionWork,
  previewFlexiblePayment,
  recordAdminEmiPayment,
  recordOverdueWaiver,
  validateAdminFacilityPayment,
  computePayoffSettlementBounds,
  useAdminEmiPayments,
  canCloseFacilityFile,
  closeFacilityFile,
  getFinanceFacilityMeta,
  markFacilitySeized,
  releaseFacilitySeized,
  economicInterestForFacility,
  type UnifiedCustomer,
  type CustomerInstallmentRow,
  type UnifiedLoanFacility,
  type AdminEmiPayment,
  type AdminPaymentEntryType,
} from '../../lib/workspaceStore';
import { mediaSrc } from '../../lib/mediaUrl';
import {
  amountInIndianWords,
  downloadPaymentReceiptPdf,
  printPaymentReceipt,
  type PaymentReceiptInput,
} from '../../lib/paymentReceipt';
import { companyDisplayName, companyComposedAddress, readCompanyProfile } from '../../lib/companyProfile';

interface UserDetailsProps {
  user: UnifiedCustomer;
  onBack: () => void;
  setActiveItem?: (item: string) => void;
  onLeaveUsersView?: () => void;
}

const tabDefs = [
  { id: 'overview' as const, labelKey: 'customer.tabOverview', Icon: LayoutDashboard },
  { id: 'history' as const, labelKey: 'customer.tabHistory', Icon: History },
  { id: 'alerts' as const, labelKey: 'customer.tabAlerts', Icon: Bell },
];

type TabId = (typeof tabDefs)[number]['id'];

function statusBadgeClass(status: string) {
  if (status === 'Paid') return 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300';
  if (status === 'Due') return 'bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-300';
  return 'bg-slate-100 text-slate-700 dark:bg-zinc-800 dark:text-zinc-300';
}

function isDueDatePast(dateStr: string) {
  if (!dateStr || dateStr === '—') return false;
  const t = new Date(`${dateStr}T23:59:59`).getTime();
  const books = resolveWorkspaceAsOfMs({ allowWallClockFallback: true });
  return Number.isFinite(t) && t < books;
}

function daysFromToday(dateStr: string) {
  const t = new Date(`${dateStr}T12:00:00`).getTime();
  if (!Number.isFinite(t)) return 9999;
  const books = resolveWorkspaceAsOfMs({ allowWallClockFallback: true });
  return Math.ceil((t - books) / (24 * 60 * 60 * 1000));
}

/** Upcoming obligation counts by calendar month (non-paid schedule rows). */
function cashflowByMonth(rows: CustomerInstallmentRow[], maxMonths = 14) {
  const map = new Map<string, { count: number; amount: number }>();
  for (const r of rows) {
    if (r.status === 'Paid') continue;
    const ym = r.date.slice(0, 7);
    const o = map.get(ym) ?? { count: 0, amount: 0 };
    o.count += 1;
    o.amount += r.amount;
    map.set(ym, o);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(0, maxMonths)
    .map(([ym, v]) => ({ ym, ...v }));
}

function profileInitials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function facilityMonthsBreakdown(f: UnifiedLoanFacility) {
  const isShort = f.repaymentType === 'short_term';
  return {
    principal: f.principal,
    emi: Math.round(f.emi),
    months: f.periods,
    approxTotalEmi: Math.round(f.emi * f.periods),
    isShort,
    termDays: f.termDays,
    repaymentLabel: isShort ? `Short term · ${f.termDays ?? '—'} days` : `EMI · ${f.periods} mo`,
    emiLabel: isShort ? 'Lump sum' : 'EMI',
  };
}

function formatAdminEntryType(t?: string | null, allocation?: string | null) {
  if (allocation === 'interest') return 'Interest';
  if (allocation === 'principal') return 'Principal';
  if (allocation === 'both') return 'Interest + Principal';
  if (!t) return '—';
  const labels: Record<string, string> = {
    emi: 'EMI',
    advance_emi: 'Advance EMI',
    credit_reversal: 'Slot reversal',
    late_fee: 'Late / penal',
    lump_sum_close: 'Full close',
    manual_adjustment: 'Manual',
    mixed: 'Mixed',
    out_payment: 'Out payment',
    overdue_waive: 'Overdue waived',
  };
  return labels[t] ?? t.replace(/_/g, ' ');
}

export function UserDetails({ user, onBack, setActiveItem, onLeaveUsersView }: UserDetailsProps) {
  const { t } = useTranslation();
  const tabs = useMemo(
    () => tabDefs.map((tab) => ({ ...tab, label: t(tab.labelKey) })),
    [t]
  );
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showOverdueDetail, setShowOverdueDetail] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Opening a profile should always start at the top of the page.
  useScrollToTop([user.id, activeTab]);

  const [historySearch, setHistorySearch] = useState('');
  const [scheduleSearch, setScheduleSearch] = useState('');
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>('all');
  const [paymentFacility, setPaymentFacility] = useState<UnifiedLoanFacility | null>(null);
  const [seizeFacility, setSeizeFacility] = useState<UnifiedLoanFacility | null>(null);
  const [reduceTarget, setReduceTarget] = useState<{ facility: UnifiedLoanFacility; max: number } | null>(null);
  const canWrite = useCanWrite('customers');
  const canApproveWaivers = useCanApproveWaivers();
  const smsRemindersAllowed = planAllowsItem(SMS_REMINDER_FEATURE_ID);
  const adminPayments = useAdminEmiPayments();

  const principalPendingOnly = user.principalOutstanding ?? Math.max(0, user.pendingLoan - (user.accruedLateFees ?? 0));

  const schedule = useMemo(() => {
    const all = getInstallmentScheduleForCustomer(user);
    if (selectedFacilityId === 'all') return all;
    return all.filter((r) => r.recordId === selectedFacilityId);
  }, [user, selectedFacilityId]);

  const paidScheduleRows = useMemo(() => schedule.filter((r) => r.status === 'Paid'), [schedule]);
  const monthBuckets = useMemo(() => cashflowByMonth(schedule), [schedule]);

  const myAdminPayments = useMemo(
    () => adminPayments.filter((p) => p.customerId === user.id),
    [adminPayments, user.id]
  );

  const filteredAdmin = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    if (!q) return myAdminPayments;
    return myAdminPayments.filter((p) =>
      [p.facilityTitle, p.note ?? '', p.recordId, String(p.amount), String(p.lateFeeAmount ?? ''), p.entryType ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [myAdminPayments, historySearch]);

  const filteredPaidRows = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    if (!q) return paidScheduleRows;
    return paidScheduleRows.filter((r) =>
      [r.facility, r.date, String(r.amount), r.status].join(' ').toLowerCase().includes(q)
    );
  }, [paidScheduleRows, historySearch]);

  const filteredSchedule = useMemo(() => {
    const q = scheduleSearch.trim().toLowerCase();
    if (!q) return schedule;
    return schedule.filter((r) =>
      [r.facility, r.date, String(r.amount), r.status, r.kind].join(' ').toLowerCase().includes(q)
    );
  }, [schedule, scheduleSearch]);

  const customerLedgerTotals = useMemo(() => {
    void adminPayments;
    const totalCashPosted = myAdminPayments.reduce((s, p) => s + Math.max(0, Number(p.amount) || 0), 0);
    const rowsWithInterestSplit = myAdminPayments.filter((p) => p.interestPortion != null).length;
    let contractInterestRecognized = 0;
    let ledgerInterestSplits = 0;
    for (const f of user.facilities) {
      const snap = getFacilityCollectionSnapshot(f);
      contractInterestRecognized += snap.interestRecognizedToDate;
      // After full settlement, show economic interest (cash − principal), not inflated linear splits.
      if (snap.isScheduleComplete && f.kind === 'finance') {
        ledgerInterestSplits += economicInterestForFacility(f, adminPayments);
      } else {
        ledgerInterestSplits += myAdminPayments
          .filter((p) => p.recordId === f.recordId)
          .reduce((s, p) => s + (Number(p.interestPortion) || 0), 0);
      }
    }
    return {
      totalCashPosted,
      ledgerInterestSplits,
      rowsWithInterestSplit,
      totalLedgerRows: myAdminPayments.length,
      contractInterestRecognized,
    };
  }, [myAdminPayments, user.facilities, adminPayments]);

  const nextDueRow = useMemo(() => schedule.find((r) => r.status === 'Due'), [schedule]);
  const overdue =
    user.pendingEmi.dueDate &&
    user.pendingEmi.dueDate !== '—' &&
    isDueDatePast(user.pendingEmi.dueDate) &&
    user.pendingLoan > 0;

  const alertRows = useMemo(() => {
    const overdueList = schedule.filter((r) => r.status === 'Due' && isDueDatePast(r.date));
    const dueSoon = schedule.filter((r) => {
      if (r.status !== 'Due' && r.status !== 'Scheduled') return false;
      const d = daysFromToday(r.date);
      return d >= 0 && d <= 14;
    });
    return { overdueList, dueSoon };
  }, [schedule]);

  const overdueAnalysis = useMemo(
    () => computeCustomerOverdueAnalysis(user, adminPayments, readOverduePenaltyConfig()),
    [user, adminPayments]
  );

  const overdueByFacility = useMemo(() => {
    const byRecord = new Map<string, { remaining: number; unpaidCount: number }>();
    for (const r of overdueAnalysis.ledgerRows) {
      const cur = byRecord.get(r.recordId) ?? { remaining: 0, unpaidCount: 0 };
      cur.remaining += r.remainingBalance;
      if (r.status === 'unpaid_overdue') cur.unpaidCount += 1;
      byRecord.set(r.recordId, cur);
    }
    return user.facilities
      .map((facility) => {
        const agg = byRecord.get(facility.recordId);
        return { facility, remaining: Math.round((agg?.remaining ?? 0) * 100) / 100, unpaidCount: agg?.unpaidCount ?? 0 };
      })
      .filter((x) => x.remaining > 0.004);
  }, [overdueAnalysis.ledgerRows, user.facilities]);

  const { overdueDailyGrowth, unpaidEmiCount } = useMemo(() => {
    const unpaid = overdueAnalysis.ledgerRows.filter((r) => r.status === 'unpaid_overdue');
    return {
      overdueDailyGrowth: unpaid.reduce((s, r) => s + (Number(r.emiAmount) || 0) / 365, 0),
      unpaidEmiCount: unpaid.length,
    };
  }, [overdueAnalysis.ledgerRows]);

  // Branded PDF/Excel export rows per tab.
  const overviewExportRows: ExportRow[] = user.facilities.map((f) => {
    const br = facilityMonthsBreakdown(f);
    const colSnap = getFacilityCollectionSnapshot(f);
    return {
      Facility: f.title,
      Type: f.kind === 'finance' ? 'Finance' : 'Hand loan',
      Principal: br.principal,
      OutstandingPrincipal: colSnap.handPrincipalOutstanding ?? colSnap.principalOutstandingLinear,
      InterestDue: colSnap.handInterestDue ?? colSnap.lifetimeInterestEstimate,
      AmountDue: colSnap.currentDueAmount,
      EMI: br.emi,
      Months: br.months,
      EstTotalEMI: br.approxTotalEmi,
    };
  });

  const historyExportRows: ExportRow[] = myAdminPayments.map((p) => ({
    Date: new Date(p.paidAt).toLocaleDateString('en-IN'),
    Facility: p.facilityTitle,
    Type: formatAdminEntryType(p.entryType, p.paymentAllocation),
    Receipt: p.amount,
    BalanceAfter: p.carriedAfter != null ? p.carriedAfter : '—',
    LateFee: p.lateFeeAmount ?? 0,
    Closed: p.facilityPayoff ? 'Yes' : 'No',
    Note: p.note ?? '',
  }));

  const alertsExportRows: ExportRow[] = [
    ...alertRows.overdueList.map((r) => ({
      Severity: 'Overdue',
      Facility: r.facility,
      DueDate: r.date,
      Amount: r.amount,
    })),
    ...alertRows.dueSoon.map((r) => ({
      Severity: `Due in ${daysFromToday(r.date)}d`,
      Facility: r.facility,
      DueDate: r.date,
      Amount: r.amount,
    })),
  ];

  const overdueExportRows: ExportRow[] = overdueAnalysis.ledgerRows
    .filter((r) => r.originalAmount > 0 || r.remainingBalance > 0)
    .map((r) => ({
    Facility: r.facility,
    Installment: r.installmentNo,
    DueDate: r.dueDate,
    PaidDate: r.paidDate || '',
    OverdueDays: r.overdueDays,
    EmiAmount: r.emiAmount,
    OriginalOverdue: r.originalAmount,
    Waived: r.waivedAmount,
    Collected: r.collectedAmount,
    Remaining: r.remainingBalance,
    Status: r.status === 'paid_late' ? 'Paid late' : 'Unpaid overdue',
  }));

  const overdueLegacyExportRows: ExportRow[] = overdueAnalysis.rows.map((r) => ({
    Facility: r.facility,
    Installment: r.installmentNo,
    DueDate: formatDisplayDate(r.dueDate),
    PaidDate: r.paidDate ? formatDisplayDate(r.paidDate) : '—',
    OverdueDays: r.overdueDays,
    EmiAmount: r.emiAmount,
    Penalty: r.penaltyAmount,
    Status: r.status === 'paid_late' ? 'Paid late' : 'Unpaid overdue',
  }));

  const safeName = user.name.replace(/[^a-z0-9]+/gi, '-');

  const scheduleExportRows: ExportRow[] = schedule.map((r, i) => ({
    SNo: i + 1,
    Facility: r.facility,
    Type: r.kind === 'finance' ? 'Finance' : 'Hand loan',
    DueDate: new Date(r.date).toLocaleDateString('en-IN'),
    Amount: r.amount,
    Status: r.status,
  }));

  const sendSmsReminder = async () => {
    if (!smsRemindersAllowed) {
      toast.error('Your subscription plan does not include SMS payment reminders.');
      return;
    }
    if (!user.phone?.trim()) {
      toast.error('Add a customer phone number to send SMS reminders.');
      return;
    }
    if (!user.pendingEmi.amount || !user.pendingEmi.dueDate || user.pendingEmi.dueDate === '—') {
      toast.info('No upcoming EMI due date to remind about.');
      return;
    }
    setSendingReminder(true);
    try {
      const dueFacility =
        user.facilities.find((f) => f.title === nextDueRow?.facility) || user.facilities[0];
      await smsApi.sendPaymentReminder({
        phone: user.phone,
        customerName: user.name,
        amount: user.pendingEmi.amount,
        dueDate: user.pendingEmi.dueDate,
        haNumber: dueFacility?.haNumber,
        recordId: dueFacility?.recordId,
        kind: dueFacility?.kind,
        facilityTitle: nextDueRow?.facility,
        daysBefore: daysUntilDue(user.pendingEmi.dueDate) ?? undefined,
      });
      toast.success(
        `Payment reminder SMS sent for ₹${Math.round(user.pendingEmi.amount).toLocaleString('en-IN')} (total pending EMIs).`
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send SMS reminder.');
    } finally {
      setSendingReminder(false);
    }
  };

  const openApplication = async (f: UnifiedLoanFacility) => {
    const list = f.kind === 'finance' ? readFinanceRecords() : readHandLoanRecords();
    const rec = list.find((r) => r.id === f.recordId);
    const data = rec?.data ?? { customerName: user.name, mobileNumber: user.phone };
    setPdfBusy(true);
    try {
      await exportLoanApplicationPdf(data as Record<string, unknown>, f.kind);
    } catch {
      toast.error('Could not generate PDF. Please try again.');
    } finally {
      setPdfBusy(false);
    }
  };

  const startRefinance = (f: UnifiedLoanFacility) => {
    if (!canWrite || f.kind !== 'finance') return;
    queueRefinance(f.recordId);
    onLeaveUsersView?.();
    if (setActiveItem) setActiveItem('finance');
    toast.info('Opening refinance — enter new amount and interest.');
  };

  const handleCloseFile = async (f: UnifiedLoanFacility) => {
    const precheck = canCloseFacilityFile(f);
    if (!precheck.ok) {
      toast.error(precheck.error);
      return;
    }
    if (!window.confirm(`Close the file for "${f.title}"? All EMIs must already be settled.`)) return;
    const result = await closeFacilityFile(f, user.id);
    if (!result.ok) toast.error(result.error);
    else toast.success('File closed — all EMIs were settled.');
  };

  const handleReleaseSeized = (f: UnifiedLoanFacility) => {
    if (!window.confirm(`Release seizure status for "${f.title}"?`)) return;
    const result = releaseFacilitySeized(f.recordId);
    if (!result.ok) toast.error(result.error);
    else toast.success('Seizure status cleared.');
  };

  const financeFacilities = useMemo(
    () => user.facilities.filter((f) => f.kind === 'finance'),
    [user.facilities],
  );

  const maxBucket = Math.max(1, ...monthBuckets.map((b) => b.amount));

  return (
    <div className="app-page min-w-0 max-w-full space-y-5 overflow-x-hidden sm:space-y-6">
      {pdfBusy ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="rounded-2xl bg-white px-6 py-5 text-center shadow-xl dark:bg-zinc-900">
            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Preparing PDF…</p>
            <p className="mt-1 text-xs text-zinc-500">Please wait — opens when ready</p>
          </div>
        </div>
      ) : null}
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Customers
      </button>

      <div className="app-profile-hero">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:text-left">
          {user.avatar ? (
            <img src={mediaSrc(user.avatar)} alt="" className="app-avatar-ring h-20 w-20 shrink-0 rounded-2xl object-cover sm:h-24 sm:w-24" />
          ) : (
            <div className="app-avatar-ring flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-600/30 to-teal-600/20 text-xl font-bold text-emerald-950 dark:text-emerald-100 sm:h-24 sm:w-24 sm:text-2xl">
              {profileInitials(user.name)}
            </div>
          )}
          <div className="min-w-0 flex-1 text-center sm:text-left">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800 dark:text-emerald-400">
              {t('common.borrower')} profile
            </p>
            <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 sm:text-3xl">{user.name}</h1>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200/80 bg-white/80 px-3 py-1 text-xs font-medium text-zinc-700 shadow-sm dark:border-zinc-600 dark:bg-zinc-800/80 dark:text-zinc-200">
                <Mail className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                {user.email}
              </span>
              {user.phone ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200/80 bg-white/80 px-3 py-1 text-xs font-medium text-zinc-700 shadow-sm dark:border-zinc-600 dark:bg-zinc-800/80 dark:text-zinc-200">
                  <Phone className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                  {user.phone}
                </span>
              ) : null}
              <span className="inline-flex items-center rounded-full bg-emerald-100/80 px-3 py-1 text-xs font-semibold text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300">
                {user.facilities.length} {user.facilities.length === 1 ? 'facility' : 'facilities'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="app-segment-tabs">
        {tabs.map((tab) => {
          const Icon = tab.Icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`app-segment-tab ${active ? 'app-segment-tab-active' : 'app-segment-tab-inactive'}`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-5 sm:space-y-6">
          <div className="flex flex-col gap-2 border-b border-zinc-200/80 pb-3 dark:border-zinc-700 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="app-section-label">{t('customer.tabOverview')}</p>
              <h3 className="app-section-heading mt-0.5">{t('customer.accountSummary')}</h3>
            </div>
            <ExportMenu title={`Customer Overview — ${user.name}`} excelFilename={`overview-${safeName}.xls`} rows={overviewExportRows} whatsappPhone={user.phone} className="shrink-0 self-start sm:ml-auto" />
          </div>
          <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4 lg:gap-3">
            <StatsCard
              title={t('common.principal')}
              titleLong={t('customers.totalPrincipal')}
              value={`₹${user.totalLoan.toLocaleString('en-IN')}`}
              icon={Wallet}
              color="text-zinc-600"
              accent="border-l-zinc-500"
            />
            <StatsCard
              title={t('common.collected')}
              titleLong={t('customers.amountCollected')}
              value={`₹${user.paidLoan.toLocaleString('en-IN')}`}
              icon={CreditCard}
              color="text-emerald-700"
              accent="border-l-emerald-600"
            />
            <StatsCard
              title={t('common.outstanding')}
              titleLong={t('customers.principalOutstanding')}
              value={`₹${principalPendingOnly.toLocaleString('en-IN')}`}
              subValue={
                (user.accruedLateFees ?? 0) > 0
                  ? `+ ₹${(user.accruedLateFees ?? 0).toLocaleString('en-IN')} late / penal`
                  : undefined
              }
              icon={TrendingUp}
              color="text-amber-700"
              accent="border-l-amber-500"
            />
            <StatsCard
              title={t('reports.amtDue')}
              titleLong={t('customers.totalDue')}
              value={`₹${user.pendingLoan.toLocaleString('en-IN')}`}
              subValue={
                user.facilities.some((f) => f.kind === 'handloan')
                  ? 'Principal outstanding + interest due'
                  : undefined
              }
              icon={Banknote}
              color="text-indigo-700"
              accent="border-l-indigo-600"
            />
          </div>

          <div
            className={`rounded-xl border p-4 shadow-[0_1px_3px_rgba(0,0,0,0.03)] sm:p-5 ${
              overdue ? 'border-rose-200/90 bg-gradient-to-br from-rose-50 to-white dark:border-rose-900/50 dark:from-rose-950/30 dark:to-zinc-900' : 'border-amber-200/90 bg-gradient-to-br from-amber-50/90 to-white dark:border-amber-900/40 dark:from-amber-950/20 dark:to-zinc-900'
            }`}
          >
            {user.facilities.length > 1 ? (
              <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <label htmlFor="admin-loan-select" className="text-xs font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-400">
                  {t('common.selectLoan')}
                </label>
                <select
                  id="admin-loan-select"
                  value={selectedFacilityId}
                  onChange={(e) => setSelectedFacilityId(e.target.value)}
                  className="w-full max-w-md rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 sm:w-auto"
                >
                  <option value="all">All loans</option>
                  {user.facilities.map((f) => (
                    <option key={f.recordId} value={f.recordId}>
                      {f.title}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className={`flex items-center gap-2 text-base font-semibold ${overdue ? 'text-red-900 dark:text-red-400' : 'text-amber-900 dark:text-amber-300'}`}>
                  <CalendarClock className="h-5 w-5 shrink-0" />
                  {t('customer.nextInstallment')}
                </h3>
                <p className="mt-1 text-sm text-zinc-800 dark:text-zinc-200">
                  <span className="font-medium text-zinc-500 dark:text-zinc-400">Date: </span>
                  <strong className="font-mono">
                    {nextDueRow?.date ||
                      (user.pendingEmi.dueDate && user.pendingEmi.dueDate !== '—'
                        ? user.pendingEmi.dueDate
                        : '—')}
                  </strong>
                </p>
                <p className="mt-0.5 text-sm text-zinc-800 dark:text-zinc-200">
                  <span className="font-medium text-zinc-500 dark:text-zinc-400">This EMI: </span>
                  {nextDueRow && nextDueRow.amount > 0 ? (
                    <strong className="font-mono">₹{nextDueRow.amount.toLocaleString('en-IN')}</strong>
                  ) : (
                    <span className="text-zinc-600 dark:text-zinc-400">—</span>
                  )}
                </p>
                <p className="mt-0.5 text-sm text-zinc-800 dark:text-zinc-200">
                  <span className="font-medium text-zinc-500 dark:text-zinc-400">Total pending EMIs: </span>
                  {user.pendingEmi.amount > 0 ? (
                    <strong className="font-mono text-rose-800 dark:text-rose-300">
                      ₹{user.pendingEmi.amount.toLocaleString('en-IN')}
                    </strong>
                  ) : (
                    <span className="text-zinc-600 dark:text-zinc-400">No upcoming EMI</span>
                  )}
                </p>
                {user.pendingEmi.amount > 0 && nextDueRow && user.pendingEmi.amount > nextDueRow.amount ? (
                  <p className="mt-1 text-[11px] text-amber-800 dark:text-amber-300">
                    Includes earlier unpaid installments (SMS uses this total).
                  </p>
                ) : null}
                {nextDueRow ? (
                  <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                    {nextDueRow.facility} · {nextDueRow.kind === 'finance' ? 'Finance' : 'Hand loan'}
                  </p>
                ) : null}
              </div>
              {canWrite && nextDueRow ? (
                <button
                  type="button"
                  onClick={() => {
                    const f = user.facilities.find((x) => x.recordId === nextDueRow.recordId);
                    if (f) setPaymentFacility(f);
                  }}
                  className="rounded-lg bg-emerald-600 px-4 py-2.5 text-xs font-bold text-white shadow-md transition hover:bg-emerald-700 hover:shadow-lg"
                >
                  {t('customer.collectEmis')}
                </button>
              ) : null}
            </div>
          </div>

          <div className="app-panel">
            <div className="mb-4 flex flex-col gap-1 border-b border-zinc-100 pb-3 dark:border-zinc-800 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="app-section-label">{t('common.facilities')}</p>
                <h3 className="app-section-heading mt-0.5 flex items-center gap-2">
                  <Layers className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
                  {t('customer.facilityTable')}
                </h3>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('customer.facilityTableSub')}</p>
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-zinc-100 dark:border-zinc-800">
              <table className="app-data-table min-w-full text-left text-sm">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left">{t('customer.collectionCol')}</th>
                    <th className="px-3 py-2">Facility</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Principal</th>
                    <th className="px-3 py-2 text-right">EMI</th>
                    <th className="px-3 py-2 text-right">Tenor</th>
                    <th className="px-3 py-2 text-right">Σ payable</th>
                    <th className="px-3 py-2 text-center">Application</th>
                  </tr>
                </thead>
                <tbody>
                  {user.facilities.map((f) => {
                    const br = facilityMonthsBreakdown(f);
                    const colSnap = getFacilityCollectionSnapshot(f);
                    const meta = f.kind === 'finance' ? getFinanceFacilityMeta(f.recordId) : null;
                    const isClosed = colSnap.isLedgerClosed || meta?.closedOnRecord;
                    const isSeized = meta?.seized ?? false;
                    const canRecordHere = colSnap.canOpenPaymentUi && facilityHasActionableCollectionWork(colSnap);
                    const recordTitle = !colSnap.canOpenPaymentUi
                      ? 'This facility already has a closure row — no further ledger entries.'
                      : !facilityHasActionableCollectionWork(colSnap)
                        ? 'All EMIs are settled and there is no balance or late charges on file.'
                        : 'Collect whatever EMI amount the customer paid — balance carries to next month';
                    const recordLabel = !colSnap.canOpenPaymentUi ? 'Closed' : !facilityHasActionableCollectionWork(colSnap) ? t('customer.emisComplete') : t('customer.collectEmis');
                    return (
                      <tr key={`${f.recordId}-${f.kind}`}>
                        <td className="px-3 py-2 text-left">
                          <div className="flex flex-wrap justify-start gap-1">
                            {isClosed && f.kind === 'finance' && canWrite ? (
                              <button
                                type="button"
                                onClick={() => startRefinance(f)}
                                title="New finance on same customer and vehicle — enter fresh amount and interest"
                                className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs font-semibold text-violet-800 hover:bg-violet-100"
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                                Refinance
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={!canRecordHere || !canWrite}
                                title={!canWrite ? 'Read-only access' : recordTitle}
                                onClick={() => { if (canWrite) setPaymentFacility(f); }}
                                className={`whitespace-nowrap rounded-md px-3 py-2 text-xs font-bold shadow-sm ${
                                  canRecordHere && canWrite
                                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                    : 'cursor-not-allowed bg-gray-200 text-gray-500'
                                }`}
                              >
                                {recordLabel}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          <div>{f.title}</div>
                          {isSeized && meta?.seizedReason ? (
                            <p className="mt-1 max-w-xs text-[10px] leading-snug text-rose-700">
                              Seized: {meta.seizedReason}
                              {meta.seizedDate ? ` · ${meta.seizedDate}` : ''}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {f.kind === 'finance' ? 'Finance' : br.repaymentLabel}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {isClosed ? (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800">
                                <Lock className="h-3 w-3" />
                                Closed
                              </span>
                            ) : colSnap.remainingInstallments === 0 && !facilityHasActionableCollectionWork(colSnap) ? (
                              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">Ready to close</span>
                            ) : (
                              <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-600">Active</span>
                            )}
                            {isSeized ? (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-800">
                                <Ban className="h-3 w-3" />
                                Seized
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          <div>₹{br.principal.toLocaleString('en-IN')}</div>
                          {f.kind === 'handloan' && colSnap.handPrincipalOutstanding != null ? (
                            <div className="text-[10px] font-medium text-amber-800">
                              Out. ₹{colSnap.handPrincipalOutstanding.toLocaleString('en-IN')}
                              {colSnap.handInterestDue != null && colSnap.handInterestDue > 0
                                ? ` · Int. ₹${colSnap.handInterestDue.toLocaleString('en-IN')}`
                                : ''}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          ₹{br.emi.toLocaleString('en-IN')}
                          <span className="ml-1 text-[10px] text-gray-500">{br.emiLabel}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {br.isShort ? `${br.termDays ?? '—'} days` : `${br.months} mo`}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-gray-600">₹{br.approxTotalEmi.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => openApplication(f)}
                            disabled={pdfBusy}
                            title="View loan application (PDF)"
                            className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
                          >
                            <FileSignature className="h-3.5 w-3.5" />
                            {pdfBusy ? 'Loading…' : 'View'}
                          </button>
                        </td>
                        </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-rose-200/90 bg-gradient-to-br from-rose-50/90 to-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.03)] dark:border-rose-900/50 dark:from-rose-950/25 dark:to-zinc-900 sm:p-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-3">
              <h3 className="flex items-center gap-2 whitespace-nowrap text-base font-semibold text-rose-950 dark:text-rose-200">
                <Clock className="h-5 w-5 shrink-0" />
                Overdue charges
              </h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowOverdueDetail(true)}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-900 hover:bg-rose-50 sm:flex-none"
                >
                  <Eye className="h-3.5 w-3.5" />
                  {t('customer.viewMissedDates')}
                </button>
                {canWrite && smsRemindersAllowed ? (
                  <button
                    type="button"
                    disabled={sendingReminder || !user.phone}
                    onClick={() => void sendSmsReminder()}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-emerald-300 bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 sm:flex-none"
                  >
                    {sendingReminder ? '…' : t('customer.smsReminder')}
                  </button>
                ) : null}
              </div>
            </div>
            {overdueAnalysis.totalPenaltyAmount > 0 ? (
              <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                <div className="rounded-lg border border-rose-100 bg-white/80 p-3 dark:border-rose-900/40 dark:bg-zinc-900/60">
                  <dl className="space-y-1.5 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-zinc-600 dark:text-zinc-400">Late charges so far</dt>
                      <dd className="font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
                        {formatInrPaise(overdueAnalysis.totalPenaltyAmount)}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-zinc-600 dark:text-zinc-400">− Already collected</dt>
                      <dd className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
                        {formatInrPaise(overdueAnalysis.totalCollected)}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-zinc-600 dark:text-zinc-400">− Reduced by admin</dt>
                      <dd className="font-semibold tabular-nums text-violet-700 dark:text-violet-300">
                        {formatInrPaise(overdueAnalysis.totalWaived)}
                      </dd>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3 border-t border-rose-100 pt-2 dark:border-rose-900/40">
                      <dt className="font-semibold text-rose-950 dark:text-rose-200">Customer must pay</dt>
                      <dd
                        className={`text-xl font-bold tabular-nums ${
                          overdueAnalysis.totalRemaining > 0
                            ? 'text-rose-700 dark:text-rose-300'
                            : 'text-emerald-700 dark:text-emerald-300'
                        }`}
                      >
                        {formatInrPaise(overdueAnalysis.totalRemaining)}
                      </dd>
                    </div>
                  </dl>
                  <p className="mt-2 text-[11px] leading-snug text-zinc-500 dark:text-zinc-400">
                    Charge per late EMI = days late × EMI ÷ 365.{' '}
                    {unpaidEmiCount > 0
                      ? `${unpaidEmiCount} EMI${unpaidEmiCount === 1 ? ' is' : 's are'} still unpaid, so this grows by about ${formatInrPaise(overdueDailyGrowth)} every day until ${unpaidEmiCount === 1 ? 'it is' : 'they are'} paid.`
                      : 'No EMI is unpaid right now, so this amount will not grow.'}
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-rose-900/80 dark:text-rose-300/80">By loan</p>
                  {overdueByFacility.length === 0 ? (
                    <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300">
                      All overdue charges are cleared.
                    </p>
                  ) : (
                    overdueByFacility.map(({ facility, remaining, unpaidCount }) => (
                      <div
                        key={facility.recordId}
                        className="flex flex-col gap-2 rounded-lg border border-rose-100 bg-white/80 p-3 dark:border-rose-900/40 dark:bg-zinc-900/60 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{facility.title}</p>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400">
                            <span className="font-semibold tabular-nums text-rose-700 dark:text-rose-300">{formatInrPaise(remaining)}</span>{' '}
                            overdue
                            {unpaidCount > 0 ? ` · ${unpaidCount} unpaid EMI${unpaidCount === 1 ? '' : 's'}` : ''}
                          </p>
                        </div>
                        {canWrite ? (
                          <div className="flex shrink-0 gap-2">
                            {canApproveWaivers ? (
                              <button
                                type="button"
                                onClick={() => setReduceTarget({ facility, max: remaining })}
                                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-900 hover:bg-violet-100 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-200 dark:hover:bg-violet-900/40 sm:flex-none"
                              >
                                <TrendingDown className="h-3.5 w-3.5" />
                                Reduce
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => setPaymentFacility(facility)}
                              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-zinc-900 dark:text-emerald-300 dark:hover:bg-emerald-950/30 sm:flex-none"
                            >
                              <Wallet className="h-3.5 w-3.5" />
                              Collect
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ))
                  )}
                  {canWrite && !canApproveWaivers && overdueByFacility.length > 0 ? (
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                      Only the owner or an admin/manager can reduce overdue charges.
                    </p>
                  ) : null}
                  <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                    {overdueAnalysis.unpaidOverdueDays} unpaid late day{overdueAnalysis.unpaidOverdueDays === 1 ? '' : 's'}
                    {overdueAnalysis.paidLateCount > 0
                      ? ` · ${overdueAnalysis.paidLateCount} EMI${overdueAnalysis.paidLateCount === 1 ? '' : 's'} paid late earlier`
                      : ''}
                    {' — '}
                    tap “{t('customer.viewMissedDates')}” to see each one.
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-xs text-emerald-800 dark:text-emerald-300">{t('customer.noOverdue')}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-3">
            <StatsCard
              title="Cash in"
              titleLong={t('customer.cashCollected')}
              value={`₹${customerLedgerTotals.totalCashPosted.toLocaleString('en-IN')}`}
              icon={CreditCard}
              color="text-emerald-700"
              accent="border-l-emerald-600"
            />
            <StatsCard
              title="Interest"
              titleLong={t('customer.interestRecognized')}
              value={`₹${customerLedgerTotals.contractInterestRecognized.toLocaleString('en-IN')}`}
              icon={Banknote}
              color="text-zinc-600"
              accent="border-l-zinc-500"
            />
            <StatsCard
              title="Ledger int."
              titleLong={t('customer.ledgerInterest')}
              value={`₹${customerLedgerTotals.ledgerInterestSplits.toLocaleString('en-IN')}`}
              icon={Layers}
              color="text-violet-700"
              accent="border-l-violet-600"
            />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-zinc-100">{t('customer.upcomingCashflow')}</h3>
            {monthBuckets.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-zinc-400">No unpaid installments in the model.</p>
            ) : (
              <div className="space-y-2">
                {monthBuckets.map((b) => (
                  <div key={b.ym} className="flex items-center gap-3">
                    <span className="w-20 shrink-0 font-mono text-xs text-gray-600 dark:text-zinc-400">{b.ym}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-zinc-800">
                      <div
                        className="h-full rounded-full bg-emerald-600/90"
                        style={{ width: `${Math.min(100, (b.amount / maxBucket) * 100)}%` }}
                      />
                    </div>
                    <span className="shrink-0 text-xs text-gray-600 dark:text-zinc-400">
                      {b.count} × ₹{b.amount.toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">{t('customer.installmentSchedule')}</h3>
              <div className="flex flex-wrap items-center gap-2">
                <PageSearchBar
                  id="user-schedule-search"
                  value={scheduleSearch}
                  onChange={setScheduleSearch}
                  placeholder="Filter schedule…"
                  className="max-w-full sm:max-w-xs"
                />
                <ExportMenu
                  title={`Installment schedule - ${user.name}`}
                  excelFilename={`installment-schedule-${safeName}`}
                  rows={scheduleExportRows}
                  className="shrink-0"
                  disabled={scheduleExportRows.length === 0}
                  whatsappPhone={user.phone}
                />
              </div>
            </div>
            <ScheduleTable
              rows={filteredSchedule}
              customer={user}
              payments={myAdminPayments}
              emptyMessage="No schedule rows match your filter."
            />
          </div>

          <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900 sm:p-5">
            <div className="mb-4 border-b border-zinc-100 pb-3 dark:border-zinc-700">
              <h3 className="flex items-center gap-2 text-base font-semibold text-zinc-900 dark:text-zinc-100">
                <Lock className="h-5 w-5 text-violet-700 dark:text-violet-400" />
                File closure &amp; seizure
              </h3>
            </div>

            {financeFacilities.length === 0 ? (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">No vehicle finance facilities on this profile.</p>
            ) : (
              <div className="space-y-4">
                {financeFacilities.map((f) => {
                  const colSnap = getFacilityCollectionSnapshot(f);
                  const meta = getFinanceFacilityMeta(f.recordId);
                  const isClosed = colSnap.isLedgerClosed || meta?.closedOnRecord;
                  const isSeized = meta?.seized ?? false;
                  const closeCheck = canCloseFacilityFile(f);
                  const canCloseHere = canWrite && closeCheck.ok;

                  return (
                    <div
                      key={f.recordId}
                      className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="font-semibold text-zinc-900 dark:text-zinc-100">{f.title}</p>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {isClosed ? (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800">
                                <Lock className="h-3 w-3" />
                                Closed
                              </span>
                            ) : colSnap.remainingInstallments === 0 && !facilityHasActionableCollectionWork(colSnap) ? (
                              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
                                Ready to close
                              </span>
                            ) : (
                              <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-semibold text-zinc-700">
                                {colSnap.remainingInstallments} EMI(s) remaining
                              </span>
                            )}
                            {isSeized ? (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-800">
                                <Ban className="h-3 w-3" />
                                Seized
                              </span>
                            ) : null}
                          </div>
                          {isSeized && meta?.seizedReason ? (
                            <p className="mt-2 text-xs leading-relaxed text-rose-800">
                              <span className="font-semibold">Reason:</span> {meta.seizedReason}
                              {meta.seizedDate ? ` · ${meta.seizedDate}` : ''}
                            </p>
                          ) : null}
                          {!isClosed && !closeCheck.ok ? (
                            <p className="mt-2 text-xs text-amber-800">{closeCheck.error}</p>
                          ) : null}
                        </div>

                        {canWrite && !isClosed ? (
                          <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                            <button
                              type="button"
                              disabled={!canCloseHere}
                              title={canCloseHere ? 'Close file — all EMIs are settled' : closeCheck.error}
                              onClick={() => handleCloseFile(f)}
                              className={`inline-flex w-full items-center justify-center gap-1.5 rounded-lg border px-4 py-2.5 text-sm font-semibold sm:w-auto ${
                                canCloseHere
                                  ? 'border-violet-300 bg-violet-600 text-white hover:bg-violet-700'
                                  : 'cursor-not-allowed border-zinc-200 bg-zinc-100 text-zinc-400'
                              }`}
                            >
                              <Lock className="h-4 w-4" />
                              Close file
                            </button>
                            {isSeized ? (
                              <button
                                type="button"
                                onClick={() => handleReleaseSeized(f)}
                                className="inline-flex w-full items-center justify-center rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 sm:w-auto"
                              >
                                Clear seizure
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setSeizeFacility(f)}
                                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-rose-300 bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 sm:w-auto"
                              >
                                <Ban className="h-4 w-4" />
                                Mark seized
                              </button>
                            )}
                          </div>
                        ) : isClosed ? (
                          <p className="text-xs font-medium text-violet-700">This file is closed.</p>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <PageSearchBar
              id="user-history-search"
              value={historySearch}
              onChange={setHistorySearch}
              placeholder="Search payments, facility, dates…"
              className="max-w-full sm:max-w-xl"
            />
            <ExportMenu title={`Payment History — ${user.name}`} excelFilename={`history-${safeName}.xls`} rows={historyExportRows} whatsappPhone={user.phone} />
          </div>

          <div>
            <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-zinc-100">Payment ledger</h3>
            <AdminPaymentsTable rows={filteredAdmin} />
          </div>

          <div>
            <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-zinc-100">Paid installments</h3>
            <ScheduleTable
              rows={filteredPaidRows}
              customer={user}
              payments={myAdminPayments}
              emptyMessage="No paid rows yet."
            />
          </div>
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">Risk &amp; reminders</h3>
            <ExportMenu title={`Alerts — ${user.name}`} excelFilename={`alerts-${safeName}.xls`} rows={alertsExportRows} whatsappPhone={user.phone} />
          </div>
          {user.pendingLoan > 0 && user.pendingLoan / Math.max(1, user.totalLoan + (user.accruedLateFees ?? 0)) > 0.5 ? (
            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <div>
                <p className="font-semibold">High outstanding ratio</p>
                <p className="mt-1 text-amber-900/90">
                  Outstanding is over half of total principal. Review collection cadence and facility terms.
                </p>
              </div>
            </div>
          ) : null}

          <div className="rounded-lg border border-red-200 bg-red-50/80 p-4">
            <h3 className="flex items-center gap-2 font-semibold text-red-900">
              <AlertTriangle className="h-4 w-4" />
              Overdue (Due + past date)
            </h3>
            {alertRows.overdueList.length === 0 ? (
              <p className="mt-2 text-sm text-red-800/90">None in the current model.</p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm text-red-900">
                {alertRows.overdueList.map((r, i) => (
                  <li key={`${r.date}-${r.recordId}-o-${i}`}>
                    <strong>{r.facility}</strong> — ₹{r.amount.toLocaleString('en-IN')} was due {r.date}
                    {r.cumulativeDue && r.cumulativeDue > r.amount
                      ? ` · total due ₹${Math.round(r.cumulativeDue).toLocaleString('en-IN')}`
                      : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
            <h3 className="font-semibold text-amber-950">Due in the next 14 days</h3>
            {alertRows.dueSoon.length === 0 ? (
              <p className="mt-2 text-sm text-amber-900/90">No rows in this window.</p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm text-amber-950">
                {alertRows.dueSoon.map((r, i) => (
                  <li key={`${r.date}-${r.recordId}-s-${i}`}>
                    {r.status} · <strong>{r.facility}</strong> — ₹{r.amount.toLocaleString('en-IN')} on {r.date} (
                    {daysFromToday(r.date)}d)
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <h3 className="font-semibold text-gray-900 dark:text-zinc-100">Next six open installments</h3>
            <ul className="mt-2 divide-y divide-gray-100 text-sm dark:divide-zinc-700">
              {user.upcomingInstallments.length === 0 ? (
                <li className="py-2 text-gray-600 dark:text-zinc-400">None.</li>
              ) : (
                user.upcomingInstallments.map((row, i) => (
                  <li key={`${row.date}-${row.facility}-${i}`} className="flex flex-wrap justify-between gap-2 py-2 dark:text-zinc-200">
                    <span>{row.facility}</span>
                    <span className="font-mono">₹{row.amount.toLocaleString('en-IN')}</span>
                    <span className="text-gray-500 dark:text-zinc-400">{row.date}</span>
                    <span className={`rounded px-1.5 text-xs font-semibold ${statusBadgeClass(row.status)}`}>{translateStatus(row.status, t)}</span>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}

      {showOverdueDetail ? (
        <OverdueDetailModal
          user={user}
          ledgerRows={overdueAnalysis.ledgerRows}
          totalDays={overdueAnalysis.unpaidOverdueDays}
          totalRemaining={overdueAnalysis.totalRemaining}
          totalOriginal={overdueAnalysis.totalPenaltyAmount}
          exportRows={overdueExportRows.length > 0 ? overdueExportRows : overdueLegacyExportRows}
          safeName={safeName}
          whatsappPhone={user.phone}
          canWrite={canWrite}
          onClose={() => setShowOverdueDetail(false)}
        />
      ) : null}

      {paymentFacility && canWrite ? (
        <FacilityPaymentModal
          userId={user.id}
          facility={paymentFacility}
          onClose={() => setPaymentFacility(null)}
        />
      ) : null}

      {seizeFacility && canWrite ? (
        <SeizeFacilityModal facility={seizeFacility} onClose={() => setSeizeFacility(null)} />
      ) : null}

      {reduceTarget && canWrite && canApproveWaivers ? (
        <CustomerOverdueWaiveModal
          user={user}
          facility={reduceTarget.facility}
          maxAmount={reduceTarget.max}
          onClose={() => setReduceTarget(null)}
        />
      ) : null}
    </div>
  );
}

function OverdueDetailModal({
  user,
  ledgerRows,
  totalDays,
  totalRemaining,
  totalOriginal,
  exportRows,
  safeName,
  whatsappPhone,
  canWrite,
  onClose,
}: {
  user: UnifiedCustomer;
  ledgerRows: OverdueInstallmentRow[];
  totalDays: number;
  totalRemaining: number;
  totalOriginal: number;
  exportRows: ExportRow[];
  safeName: string;
  whatsappPhone?: string;
  canWrite: boolean;
  onClose: () => void;
}) {
  const [waiveFacility, setWaiveFacility] = useState<UnifiedLoanFacility | null>(null);
  const [waiveMax, setWaiveMax] = useState(0);
  const canApprove = useCanApproveWaivers();
  const displayRows = ledgerRows.filter((r) => r.originalAmount > 0 || r.remainingBalance > 0);

  const facilityRemaining = new Map<string, number>();
  for (const r of displayRows) {
    facilityRemaining.set(r.recordId, (facilityRemaining.get(r.recordId) ?? 0) + r.remainingBalance);
  }

  return (
    <MobileModalPortal onClose={onClose}>
      <div className="flex max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-bottom,0px)))] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-900 sm:max-h-[92vh] sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-zinc-100 px-4 py-3 dark:border-zinc-700 sm:px-5">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-rose-800 dark:text-rose-400">Overdue ledger</p>
            <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{user.name}</h3>
            <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
              {totalDays} unpaid overdue day{totalDays === 1 ? '' : 's'} · {formatInrPaise(totalOriginal)} original ·{' '}
              {formatInrPaise(totalRemaining)} remaining
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ExportMenu
              title={`Overdue ledger — ${user.name}`}
              excelFilename={`overdue-${safeName}.xls`}
              rows={exportRows}
              className="shrink-0"
              disabled={exportRows.length === 0}
              whatsappPhone={whatsappPhone}
            />
            <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 sm:px-5">
          {displayRows.length === 0 ? (
            <p className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 py-10 text-center text-sm text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-400">
              No overdue installments — every payment was on or before the due date.
            </p>
          ) : (
            <div className="app-table-wrap border-zinc-200">
              <table className="app-table min-w-full text-xs">
                <thead>
                  <tr>
                    <th>Facility</th>
                    <th>#</th>
                    <th>Due</th>
                    <th>Paid</th>
                    <th className="text-right">Days</th>
                    <th className="text-right">EMI</th>
                    <th className="text-right">Original</th>
                    <th className="text-right">Waived</th>
                    <th className="text-right">Collected</th>
                    <th className="text-right">Remaining</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {displayRows.map((r) => (
                    <tr key={r.id} className={r.remainingBalance > 0 ? 'bg-rose-50/50 dark:bg-rose-950/30' : undefined}>
                      <td className="font-medium text-zinc-800 dark:text-zinc-200">{r.facility}</td>
                      <td className="tabular-nums text-zinc-600 dark:text-zinc-400">{r.installmentNo}</td>
                      <td className="whitespace-nowrap tabular-nums">{formatDisplayDate(r.dueDate)}</td>
                      <td className="whitespace-nowrap tabular-nums">{r.paidDate ? formatDisplayDate(r.paidDate) : '—'}</td>
                      <td className="whitespace-nowrap text-right font-semibold tabular-nums text-rose-900 dark:text-rose-300">{r.overdueDays}</td>
                      <td className="whitespace-nowrap text-right tabular-nums">{formatInrPaise(r.emiAmount)}</td>
                      <td className="whitespace-nowrap text-right tabular-nums">{formatInrPaise(r.originalAmount)}</td>
                      <td className="whitespace-nowrap text-right tabular-nums text-violet-800 dark:text-violet-300">{formatInrPaise(r.waivedAmount)}</td>
                      <td className="whitespace-nowrap text-right tabular-nums text-emerald-800 dark:text-emerald-300">{formatInrPaise(r.collectedAmount)}</td>
                      <td className="whitespace-nowrap text-right font-semibold tabular-nums text-rose-900 dark:text-rose-300">{formatInrPaise(r.remainingBalance)}</td>
                      <td>
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            r.status === 'unpaid_overdue'
                              ? 'bg-rose-100 text-rose-900 dark:bg-rose-900/50 dark:text-rose-200'
                              : 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
                          }`}
                        >
                          {r.status === 'unpaid_overdue' ? 'Unpaid' : 'Paid late'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {canWrite && canApprove ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {[...facilityRemaining.entries()].map(([recordId, remaining]) => {
                if (remaining <= 0) return null;
                const facility = user.facilities.find((f) => f.recordId === recordId);
                if (!facility) return null;
                return (
                  <button
                    key={recordId}
                    type="button"
                    onClick={() => {
                      setWaiveFacility(facility);
                      setWaiveMax(remaining);
                    }}
                    className="rounded-lg border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-900 hover:bg-violet-100 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-200 dark:hover:bg-violet-900/40"
                  >
                    Reduce overdue — {facility.title} ({formatInrPaise(remaining)})
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
        <div className="border-t border-zinc-100 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] dark:border-zinc-700 sm:px-5 sm:pb-3">
          <button type="button" onClick={onClose} className="app-btn-secondary w-full sm:w-auto">
            Close
          </button>
        </div>
      </div>
      {waiveFacility && canWrite ? (
        <CustomerOverdueWaiveModal
          user={user}
          facility={waiveFacility}
          maxAmount={waiveMax}
          onClose={() => setWaiveFacility(null)}
        />
      ) : null}
    </MobileModalPortal>
  );
}

function CustomerOverdueWaiveModal({
  user,
  facility,
  maxAmount,
  onClose,
}: {
  user: UnifiedCustomer;
  facility: UnifiedLoanFacility;
  maxAmount: number;
  onClose: () => void;
}) {
  const balance = Math.round(maxAmount * 100) / 100;
  const [mode, setMode] = useState<'reduceBy' | 'payExactly'>('payExactly');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [paidAt, setPaidAt] = useState(defaultAdminEntryDate());
  const [busy, setBusy] = useState(false);

  const entered = value.trim() === '' ? null : Math.round(parseAmountInput(value) * 100) / 100;
  const reduction =
    entered == null ? 0 : mode === 'reduceBy' ? entered : Math.round((balance - entered) * 100) / 100;
  const newBalance = Math.round((balance - reduction) * 100) / 100;

  let problem: string | null = null;
  if (entered != null) {
    if (mode === 'payExactly' && entered >= balance) problem = 'The new amount must be less than the current overdue.';
    else if (mode === 'payExactly' && entered < 0) problem = 'The new amount cannot be negative.';
    else if (mode === 'reduceBy' && entered <= 0) problem = 'Enter how much to reduce.';
    else if (reduction > balance + 0.01) problem = `You can reduce at most ${formatInrPaise(balance)}.`;
  }
  const canSubmit = entered != null && !problem && reduction > 0 && reason.trim().length > 0 && !busy;

  const roundedDown = Math.floor(balance / 100) * 100;
  const quickTargets: { label: string; payExactly: number }[] = [
    { label: 'Waive all', payExactly: 0 },
    { label: 'Half', payExactly: Math.round((balance / 2) * 100) / 100 },
    ...(roundedDown > 0 && roundedDown < balance ? [{ label: `Round to ${formatInrPaise(roundedDown)}`, payExactly: roundedDown }] : []),
  ];
  const reasonChips = ['Goodwill', 'Customer hardship', 'Settlement agreed', 'Round-off'];

  const applyQuick = (payExactly: number) => {
    setMode('payExactly');
    setValue(String(payExactly));
  };

  const switchMode = (next: 'reduceBy' | 'payExactly') => {
    if (next === mode) return;
    if (entered != null && !problem) setValue(String(next === 'reduceBy' ? reduction : newBalance));
    setMode(next);
  };

  const submit = async () => {
    if (!canSubmit) {
      if (!reason.trim()) toast.error('A reason is required.');
      else if (problem) toast.error(problem);
      else toast.error('Enter an amount.');
      return;
    }
    setBusy(true);
    const res = await recordOverdueWaiver({
      customerId: user.id,
      recordId: facility.recordId,
      facilityTitle: facility.title,
      kind: facility.kind,
      amount: reduction,
      reason: reason.trim(),
      paidAt: adminEntryIso(paidAt),
      facility,
    });
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error || 'Could not reduce overdue.');
      return;
    }
    toast.success(`Overdue reduced by ${formatInrPaise(reduction)}. Customer now owes ${formatInrPaise(newBalance)}.`);
    onClose();
  };

  const tabBtn = (active: boolean) =>
    `flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
      active
        ? 'bg-white text-violet-900 shadow-sm dark:bg-zinc-700 dark:text-violet-100'
        : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100'
    }`;

  return (
    <MobileModalPortal onClose={onClose}>
      <div className="max-h-[92dvh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-zinc-200 bg-white p-4 shadow-2xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-800 dark:text-violet-400">Reduce overdue charges</p>
            <h3 className="truncate text-base font-semibold text-zinc-900 dark:text-zinc-100">{facility.title}</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">{user.name}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 dark:border-rose-900/50 dark:bg-rose-950/30">
          <p className="text-xs text-rose-900/80 dark:text-rose-300/80">Overdue right now</p>
          <p className="text-2xl font-bold tabular-nums text-rose-700 dark:text-rose-300">{formatInrPaise(balance)}</p>
        </div>

        <div className="mt-3 flex gap-1 rounded-lg bg-zinc-100 p-1 dark:bg-zinc-800">
          <button type="button" className={tabBtn(mode === 'payExactly')} onClick={() => switchMode('payExactly')}>
            Customer pays ₹
          </button>
          <button type="button" className={tabBtn(mode === 'reduceBy')} onClick={() => switchMode('reduceBy')}>
            Reduce by ₹
          </button>
        </div>

        <AmountInput
          id="overdue-reduce-amount"
          label={mode === 'payExactly' ? 'New overdue amount the customer should pay' : 'Amount to take off'}
          value={value}
          onChange={setValue}
          className="mt-3"
          placeholder={mode === 'payExactly' ? 'e.g. 10000' : 'e.g. 2000'}
        />

        <div className="mt-2 flex flex-wrap gap-1.5">
          {quickTargets.map((q) => (
            <button
              key={q.label}
              type="button"
              onClick={() => applyQuick(q.payExactly)}
              className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-900 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200 dark:hover:bg-violet-900/40"
            >
              {q.label}
            </button>
          ))}
        </div>

        <div
          className={`mt-3 rounded-lg border px-3 py-2 text-sm ${
            problem
              ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200'
              : 'border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300'
          }`}
        >
          {problem ? (
            problem
          ) : entered == null ? (
            'Enter an amount or tap a quick option.'
          ) : (
            <>
              <span className="tabular-nums">{formatInrPaise(balance)}</span>
              <span className="mx-1.5">→</span>
              <span className="font-bold tabular-nums text-emerald-700 dark:text-emerald-300">{formatInrPaise(newBalance)}</span>
              <span className="mt-0.5 block text-xs text-zinc-500 dark:text-zinc-400">
                Reducing by <span className="font-semibold text-violet-700 dark:text-violet-300">{formatInrPaise(reduction)}</span>
              </span>
            </>
          )}
        </div>

        <div className="mt-3 text-xs font-medium text-zinc-700 dark:text-zinc-300">
          Reason (required)
          <div className="mt-1 flex flex-wrap gap-1.5">
            {reasonChips.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setReason(c)}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                  reason === c
                    ? 'border-violet-500 bg-violet-600 text-white'
                    : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Or write your own reason"
            aria-label="Reason"
            className="mt-1.5 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
          />
        </div>

        <label className="mt-2 block text-xs font-medium text-zinc-700 dark:text-zinc-300">
          Date of reduction
          <input
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
          />
        </label>

        <p className="mt-3 text-[11px] leading-snug text-zinc-500 dark:text-zinc-400">
          The original charges stay in the history and this reduction is saved in the audit log with your name and reason.
          If EMIs are still unpaid, new late charges keep adding from tomorrow.
        </p>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void submit()}
            className="flex-1 rounded-lg bg-violet-700 py-2 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-50"
          >
            {busy ? 'Saving…' : reduction > 0 && !problem ? `Reduce by ${formatInrPaise(reduction)}` : 'Reduce overdue'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-zinc-300 px-4 py-2 text-sm font-semibold text-zinc-700 dark:border-zinc-600 dark:text-zinc-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </MobileModalPortal>
  );
}

function AdminPaymentsTable({ rows }: { rows: AdminEmiPayment[] }) {
  const anySplit = rows.some((p) => p.principalPortion != null || p.interestPortion != null);
  if (rows.length === 0) {
    return <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-400">No admin-posted EMI rows yet.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
      <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-zinc-700">
        <thead className="bg-gray-50 dark:bg-zinc-800/90">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">When</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">Facility</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">Via</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">Receipt</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">Balance after</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">Late</th>
            {anySplit ? (
              <>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">Prin. (model)</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">Int. (model)</th>
              </>
            ) : null}
            <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">Close</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">Note</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-zinc-700">
          {rows.map((p) => (
            <tr key={p.id}>
              <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-gray-700 dark:text-zinc-300">
                {new Date(p.paidAt).toLocaleString()}
              </td>
              <td className="px-4 py-2 text-gray-900 dark:text-zinc-100">{p.facilityTitle}</td>
              <td className="px-4 py-2 text-xs text-gray-600 dark:text-zinc-300">{formatAdminEntryType(p.entryType, p.paymentAllocation)}</td>
              <td className="px-4 py-2 text-xs capitalize text-gray-600 dark:text-zinc-300">
                {paymentViaDisplayLabel(p.paymentVia, p.companyBankLabel, p.cashAmount, p.bankAmount)}
              </td>
              <td className="px-4 py-2 text-right font-mono dark:text-zinc-200">₹{p.amount.toLocaleString('en-IN')}</td>
              <td className="px-4 py-2 text-right font-mono text-xs">
                {p.facilityPayoff ? '—' : p.carriedAfter != null ? (
                  p.carriedAfter > 0 ? (
                    <span className="text-amber-900">₹{p.carriedAfter.toLocaleString('en-IN')} due</span>
                  ) : p.carriedAfter < 0 ? (
                    <span className="text-emerald-800">₹{Math.abs(p.carriedAfter).toLocaleString('en-IN')} credit</span>
                  ) : (
                    <span className="text-emerald-800">Clear</span>
                  )
                ) : (
                  '—'
                )}
              </td>
              <td className="px-4 py-2 text-right font-mono text-amber-900">
                {p.lateFeeAmount ? `₹${p.lateFeeAmount.toLocaleString('en-IN')}` : '—'}
              </td>
              {anySplit ? (
                <>
                  <td className="px-4 py-2 text-right font-mono text-xs text-gray-700">
                    {p.principalPortion != null ? `₹${p.principalPortion.toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-violet-900">
                    {p.interestPortion != null ? `₹${p.interestPortion.toLocaleString('en-IN')}` : '—'}
                  </td>
                </>
              ) : null}
              <td className="px-4 py-2 text-center dark:text-zinc-300">{p.facilityPayoff ? 'Yes' : '—'}</td>
              <td className="max-w-[12rem] truncate px-4 py-2 text-gray-600 dark:text-zinc-400" title={p.note}>
                {p.penaltyDays != null ? `${p.penaltyDays}d late · ` : ''}
                {p.note ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FacilityPaymentModal({
  userId,
  facility,
  onClose,
}: {
  userId: string;
  facility: UnifiedLoanFacility;
  onClose: () => void;
}) {
  const adminLedger = useAdminEmiPayments();
  const facilityPayments = useMemo(
    () => adminLedger.filter((p) => p.recordId === facility.recordId),
    [adminLedger, facility.recordId]
  );
  const isHandLoan = facility.kind === 'handloan';
  const [paymentDate, setPaymentDate] = useState(() => defaultAdminEntryDate() || resolveWorkspaceAsOfDate());
  const [amount, setAmount] = useState('');
  const [lateFee, setLateFee] = useState('');
  const [payoff, setPayoff] = useState(false);
  const [note, setNote] = useState('');
  const [paymentVia, setPaymentVia] = useState(emptyPaymentVia());
  const [paymentAllocation, setPaymentAllocation] = useState<'interest' | 'principal' | 'both'>('interest');
  const [financePaymentKind, setFinancePaymentKind] = useState<'emi' | 'out_payment'>('emi');
  const [interestAmount, setInterestAmount] = useState('');
  const [principalAmount, setPrincipalAmount] = useState('');
  const [confirmSettlement, setConfirmSettlement] = useState(false);
  const [saving, setSaving] = useState(false);

  const lateParsed = Math.max(0, parseAmountInput(lateFee));
  const amtParsed = Math.max(0, parseAmountInput(amount));
  const emi = Math.round(facility.emi);

  const handAsOf = useMemo(() => {
    if (!isHandLoan) return undefined;
    const entryIso = paymentDate ? `${paymentDate}T12:00:00` : null;
    return handLoanLedgerAsOfMs(facility.startDate, facilityPayments, entryIso);
  }, [isHandLoan, paymentDate, facility.startDate, facilityPayments]);

  const handBalances = useMemo(() => {
    if (!isHandLoan) return null;
    void adminLedger;
    return replayHandLoanBalances({
      principal: facility.principal,
      rate: Number(facility.interestRate) || 0,
      interestType: parseHandLoanInterestType(facility.interestType),
      repaymentType: facility.repaymentType === 'short_term' ? 'short_term' : 'emi',
      startDate: facility.startDate,
      termDays: facility.termDays,
      periods: facility.periods,
      payments: facilityPayments,
      asOf: handAsOf,
    });
  }, [isHandLoan, adminLedger, facility, facilityPayments, handAsOf]);

  const snap = useMemo(() => {
    void adminLedger;
    return getFacilityCollectionSnapshot(facility);
  }, [facility, adminLedger]);

  const outPaymentDue = useMemo(() => {
    if (isHandLoan || !snap.outPaymentPending) return null;
    const opPrincipal = Math.max(0, Math.round(Number(facility.outPayment) || 0));
    if (opPrincipal <= 0) return null;
    return computeOutPaymentDue({
      outPayment: opPrincipal,
      opRate: Number(facility.opInterest) || 0,
      startDate: facility.startDate,
      asOfDate: paymentDate || resolveWorkspaceAsOfDate() || '',
    });
  }, [isHandLoan, snap.outPaymentPending, facility, paymentDate]);

  const isOutPaymentMode = !isHandLoan && financePaymentKind === 'out_payment' && !!outPaymentDue;

  useEffect(() => {
    if (!isHandLoan && snap.outPaymentPending) {
      setFinancePaymentKind('out_payment');
    } else {
      setFinancePaymentKind('emi');
    }
  }, [facility.recordId, isHandLoan, snap.outPaymentPending]);

  useEffect(() => {
    if (financePaymentKind === 'out_payment' && outPaymentDue && outPaymentDue.total > 0) {
      setAmount(String(outPaymentDue.total));
    }
  }, [financePaymentKind, outPaymentDue, paymentDate]);

  // Profile Penalty uses calculated overdue; Collect EMI must show the same due (not only logged ledger).
  const calculatedLate = useMemo(() => {
    void adminLedger;
    return computeFacilityUnpaidOverdue(facility, facilityPayments, readOverduePenaltyConfig());
  }, [facility, facilityPayments, adminLedger]);

  const loggedLateDue = Math.max(0, snap.accruedLateFees);
  const calculatedLateDue = Math.max(0, calculatedLate.penaltyAmount);
  const lateDueNow = Math.max(loggedLateDue, calculatedLateDue);
  const lateChargeGap = Math.max(0, calculatedLateDue - loggedLateDue);
  /** True when late-charges field is for clearing overdue/penalty — not logging a brand-new fee. */
  const clearingOverdueLate = lateDueNow > 0 || calculatedLate.overdueDays > 0;

  const handInterestDue = handBalances?.interestDue ?? snap.handInterestDue ?? 0;
  const handPrincipalOut = handBalances?.principalOutstanding ?? snap.handPrincipalOutstanding ?? snap.principal;
  const handDueTotal = handPrincipalOut + handInterestDue;

  const payoffBounds = useMemo(() => {
    if (!payoff) return null;
    const paidAt = paymentDate ? `${paymentDate}T12:00:00` : undefined;
    const collectTowardDue = lateDueNow > 0 ? Math.min(lateParsed, lateDueNow) : 0;
    const extraNewCharge = Math.max(0, lateParsed - lateDueNow);
    const lateCharged = (lateParsed > 0 ? lateChargeGap : 0) + extraNewCharge;
    const lateCollectedPreview = lateDueNow > 0 ? collectTowardDue : lateCharged;
    return computePayoffSettlementBounds(facility, {
      lateFeeAmount: lateCharged,
      lateFeeCollected: lateCollectedPreview,
      paidAt,
    });
  }, [payoff, facility, paymentDate, lateParsed, lateDueNow, lateChargeGap]);

  const settlementDiscountPreview = useMemo(() => {
    if (!payoffBounds) return 0;
    const collectTowardDue = lateDueNow > 0 ? Math.min(lateParsed, lateDueNow) : 0;
    const extraNewCharge = Math.max(0, lateParsed - lateDueNow);
    const lateCharged = (lateParsed > 0 ? lateChargeGap : 0) + extraNewCharge;
    const lateCollectedPreview = lateDueNow > 0 ? collectTowardDue : lateCharged;
    const receiptTotal = amtParsed + lateCollectedPreview;
    return Math.max(0, Math.round(payoffBounds.fullDue - receiptTotal));
  }, [payoffBounds, amtParsed, lateParsed, lateDueNow, lateChargeGap]);

  const paymentPreview = useMemo(() => {
    void adminLedger;
    if (isOutPaymentMode) return null;
    if (isHandLoan && !payoff) return null;
    if (payoff) {
      return previewFlexiblePayment(facility, adminLedger, { amount: amtParsed, lateFeeAmount: lateParsed, payoff: true });
    }
    if (amtParsed <= 0) return null;
    return previewFlexiblePayment(facility, adminLedger, { amount: amtParsed, lateFeeAmount: lateParsed });
  }, [facility, adminLedger, amtParsed, lateParsed, payoff, isHandLoan, isOutPaymentMode]);

  const handAfterPreview = useMemo(() => {
    if (!isHandLoan || payoff || amtParsed <= 0) return null;
    let iPart = 0;
    let pPart = 0;
    if (paymentAllocation === 'interest') {
      iPart = amtParsed;
    } else if (paymentAllocation === 'principal') {
      pPart = amtParsed;
    } else {
      iPart = Math.max(0, Math.round(parseAmountInput(interestAmount)));
      pPart = Math.max(0, Math.round(parseAmountInput(principalAmount)));
    }
    const entryIso = paymentDate ? `${paymentDate}T12:00:00` : adminEntryIso();
    return replayHandLoanBalances({
      principal: facility.principal,
      rate: Number(facility.interestRate) || 0,
      interestType: parseHandLoanInterestType(facility.interestType),
      repaymentType: facility.repaymentType === 'short_term' ? 'short_term' : 'emi',
      startDate: facility.startDate,
      termDays: facility.termDays,
      periods: facility.periods,
      payments: [
        ...facilityPayments,
        {
          paidAt: entryIso,
          amount: amtParsed,
          paymentAllocation,
          interestPortion: iPart,
          principalPortion: pPart,
        },
      ],
      asOf: handLoanLedgerAsOfMs(facility.startDate, facilityPayments, entryIso),
    });
  }, [
    isHandLoan,
    payoff,
    amtParsed,
    paymentAllocation,
    interestAmount,
    principalAmount,
    facility,
    facilityPayments,
    paymentDate,
  ]);

  const submit = async () => {
    if (saving) return;
    if (!paymentDate) {
      toast.error('Select payment date.');
      return;
    }
    const late = Math.max(0, parseAmountInput(lateFee));
    const amt = Math.max(0, parseAmountInput(amount));

    if (!payoff && late <= 0 && amt <= 0) {
      toast.error('Enter amount received.');
      return;
    }

    if (isOutPaymentMode && outPaymentDue) {
      if (payoff) {
        toast.error('Collect out payment separately before full settlement.');
        return;
      }
      if (amt + 0.5 < outPaymentDue.total) {
        toast.error(
          `Out payment due is ₹${outPaymentDue.total.toLocaleString('en-IN')} (principal ₹${outPaymentDue.principal.toLocaleString('en-IN')} + interest ₹${outPaymentDue.interest.toLocaleString('en-IN')} for ${outPaymentDue.days} day(s)).`
        );
        return;
      }
    }

    // Merge logged late balance with calculated unpaid penalty (same source as profile).
    const collectTowardDue = lateDueNow > 0 ? Math.min(late, lateDueNow) : 0;
    const extraNewCharge = Math.max(0, late - lateDueNow);
    const lateCharged = (late > 0 ? lateChargeGap : 0) + extraNewCharge;
    const lateCollected = lateDueNow > 0 ? collectTowardDue : lateCharged;

    if (payoff) {
      const pre = validateAdminFacilityPayment(facility, {
        payoff: true,
        creditedMonths: 0,
        lateFeeAmount: lateCharged,
        lateFeeCollected: lateCollected,
        amount: amt,
        flexibleEmi: false,
        paidAt: paymentDate ? `${paymentDate}T12:00:00` : adminEntryIso(),
      });
      if (!pre.ok) {
        toast.error(pre.error);
        setConfirmSettlement(false);
        return;
      }
      if (!confirmSettlement) {
        setConfirmSettlement(true);
        return;
      }
    }
    setConfirmSettlement(false);

    const receiptTotal = amt + Math.max(0, lateCollected);
    const payErr = validatePaymentVia(paymentVia, 'collection', receiptTotal);
    if (payErr) {
      toast.error(payErr);
      return;
    }

    let entryType: AdminPaymentEntryType = 'emi';
    if (isOutPaymentMode) entryType = 'out_payment';
    else if (payoff) entryType = 'lump_sum_close';
    else if (late > 0 && amt > 0) entryType = 'mixed';
    else if (late > 0) entryType = 'late_fee';
    else if (paymentPreview && paymentPreview.afterOwed < 0) entryType = 'advance_emi';

    const paidAt = new Date(`${paymentDate}T12:00:00`).toISOString();
    const bankLabel = resolveBankLabel(readCompanyBanks(), paymentVia.companyBankId);
    const payPersist = paymentViaPersistFields(paymentVia, receiptTotal);

    const outPaymentSplit =
      isOutPaymentMode && outPaymentDue
        ? {
            principalPortion: outPaymentDue.principal,
            interestPortion: Math.min(
              Math.max(0, amt - outPaymentDue.principal),
              outPaymentDue.interest
            ),
            flexibleEmi: false as const,
          }
        : {};

    const handPayload =
      isHandLoan && !payoff
        ? {
            paymentAllocation,
            interestPortion:
              paymentAllocation === 'both'
                ? Math.max(0, Math.round(parseAmountInput(interestAmount)))
                : paymentAllocation === 'interest'
                  ? amt
                  : 0,
            principalPortion:
              paymentAllocation === 'both'
                ? Math.max(0, Math.round(parseAmountInput(principalAmount)))
                : paymentAllocation === 'principal'
                  ? amt
                  : 0,
            flexibleEmi: false,
          }
        : isOutPaymentMode
          ? outPaymentSplit
          : { flexibleEmi: !payoff };

    setSaving(true);
    try {
      const result = await recordAdminEmiPayment({
        facility,
        customerId: userId,
        recordId: facility.recordId,
        facilityTitle: facility.title,
        kind: facility.kind,
        amount: amt,
        creditedMonths: 0,
        lateFeeAmount: lateCharged > 0 ? lateCharged : undefined,
        lateFeeCollected: lateCollected > 0 ? lateCollected : undefined,
        facilityPayoff: payoff,
        entryType,
        paidAt,
        note: note.trim() || undefined,
        paymentVia: payPersist.paymentVia,
        companyBankId: payPersist.companyBankId,
        companyBankLabel: bankLabel || undefined,
        paidBy: payPersist.paidBy,
        cashAmount: payPersist.cashAmount,
        bankAmount: payPersist.bankAmount,
        ...handPayload,
      });
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      toast.success(
        isOutPaymentMode ? 'Out payment saved.' : payoff ? 'Full settlement saved.' : 'Payment saved.'
      );
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const ledgerClosedView = snap.isLedgerClosed;
  const collectionIdleView = !ledgerClosedView && !facilityHasActionableCollectionWork(snap);
  const formLocked = ledgerClosedView || collectionIdleView;

  return (
    <MobileModalPortal onClose={onClose} layout="page">
      <div className="flex h-[100dvh] w-full min-w-0 max-w-full flex-col overflow-hidden bg-white sm:h-[min(92dvh,900px)] sm:max-w-lg sm:rounded-2xl sm:border sm:border-zinc-200 sm:shadow-xl">
        <header className="z-20 flex shrink-0 items-center justify-between gap-3 border-b border-zinc-200 bg-white px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top,0px))]">
          <div className="min-w-0 flex-1 overflow-hidden">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Collect EMIs</p>
            <h3 className="truncate text-sm font-semibold text-zinc-900">{facility.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-zinc-500 hover:bg-zinc-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
          <div className="mx-auto w-full max-w-md space-y-4 px-4 py-4 pb-6">
            {ledgerClosedView ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                This loan is closed.
              </p>
            ) : collectionIdleView ? (
              <p className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
                Nothing due on this loan.
              </p>
            ) : (
              <>
                <div className="w-full rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                    {isOutPaymentMode ? 'Out payment due' : 'Amount due'}
                  </p>
                  <p className="mt-1 text-xl font-bold tabular-nums text-zinc-900">
                    ₹
                    {(
                      isOutPaymentMode && outPaymentDue
                        ? outPaymentDue.total
                        : isHandLoan
                          ? handDueTotal
                          : snap.currentDueAmount
                    ).toLocaleString('en-IN')}
                  </p>
                  {isOutPaymentMode && outPaymentDue ? (
                    <p className="mt-0.5 text-xs leading-snug text-zinc-600">
                      Principal ₹{outPaymentDue.principal.toLocaleString('en-IN')} + OP interest ₹
                      {outPaymentDue.interest.toLocaleString('en-IN')}
                      {outPaymentDue.days > 0
                        ? ` · ${outPaymentDue.days} day(s) @ ₹${Number(facility.opInterest || 0)}/₹100/mo`
                        : ''}
                    </p>
                  ) : !isHandLoan && emi > 0 ? (
                    <p className="mt-0.5 text-xs text-zinc-600">
                      EMI ₹{emi.toLocaleString('en-IN')}
                      {snap.outPaymentPending ? ' · Out payment still pending' : ''}
                    </p>
                  ) : null}
                  {isHandLoan ? (
                    <div className="mt-3 grid w-full grid-cols-1 gap-2 border-t border-emerald-200/80 pt-3 sm:grid-cols-2">
                      <div className="min-w-0 rounded-lg bg-white/70 px-2.5 py-2">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">Principal out.</p>
                        <p className="mt-0.5 text-sm font-semibold tabular-nums text-zinc-900">
                          ₹{handPrincipalOut.toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div className="min-w-0 rounded-lg bg-white/70 px-2.5 py-2">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">Interest due</p>
                        <p className="mt-0.5 text-sm font-semibold tabular-nums text-zinc-900">
                          ₹{handInterestDue.toLocaleString('en-IN')}
                        </p>
                      </div>
                      <p className="text-[11px] leading-snug text-zinc-500 sm:col-span-2">
                        {facility.repaymentType === 'short_term'
                          ? 'Daily interest on outstanding principal. After the term days, unpaid balance compounds. Pay interest due, then settle principal to close.'
                          : facility.interestType === 'compound'
                            ? 'Compound interest on outstanding balance each month'
                            : 'Monthly interest on outstanding principal (not original loan amount)'}
                        {facility.interestRate != null ? ` · ₹${facility.interestRate}/₹100 / month` : ''}
                        {' · '}Balances follow payment date
                      </p>
                    </div>
                  ) : null}
                </div>

                <div className="w-full min-w-0 overflow-hidden">
                  <label className="app-label" htmlFor="collect-payment-date">
                    Payment date
                  </label>
                  <input
                    id="collect-payment-date"
                    type="date"
                    className="app-field app-field-date box-border block w-full min-w-0 max-w-full"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    disabled={formLocked}
                  />
                </div>

                {!isHandLoan && snap.outPaymentPending ? (
                  <div className="w-full min-w-0">
                    <label className="app-label" htmlFor="finance-payment-kind">
                      Payment type
                    </label>
                    <select
                      id="finance-payment-kind"
                      className="app-field box-border w-full min-w-0 max-w-full"
                      value={financePaymentKind}
                      disabled={formLocked || saving}
                      onChange={(e) => {
                        const next = e.target.value as 'emi' | 'out_payment';
                        setFinancePaymentKind(next);
                        setPayoff(false);
                        setConfirmSettlement(false);
                        if (next === 'out_payment' && outPaymentDue && outPaymentDue.total > 0) {
                          setAmount(String(outPaymentDue.total));
                        } else if (next === 'emi') {
                          setAmount('');
                        }
                      }}
                    >
                      <option value="out_payment">Out payment (down payment + OP interest)</option>
                      <option value="emi">Regular EMI</option>
                    </select>
                    <p className="mt-1 text-[11px] leading-snug text-zinc-500">
                      {financePaymentKind === 'out_payment'
                        ? 'Collect the down / out payment with interest accrued to the payment date. EMI schedule uses loan balance only (total − out payment).'
                        : 'Apply this receipt toward the monthly EMI balance.'}
                    </p>
                  </div>
                ) : null}

                {isHandLoan && !payoff ? (
                  <div className="w-full min-w-0">
                    <label className="app-label" htmlFor="payment-type">
                      Payment type
                    </label>
                    <select
                      id="payment-type"
                      className="app-field box-border w-full min-w-0 max-w-full"
                      value={paymentAllocation}
                      disabled={formLocked}
                      onChange={(e) => {
                        const next = e.target.value as 'interest' | 'principal' | 'both';
                        setPaymentAllocation(next);
                        if (next === 'principal' && handPrincipalOut > 0) {
                          setAmount(String(handPrincipalOut));
                          setPrincipalAmount(String(handPrincipalOut));
                          setInterestAmount('0');
                        } else if (next === 'interest' && handInterestDue > 0) {
                          setAmount(String(handInterestDue));
                          setInterestAmount(String(handInterestDue));
                          setPrincipalAmount('0');
                        }
                      }}
                    >
                      <option value="interest">Interest payment</option>
                      <option value="principal">Principal (outstanding)</option>
                      <option value="both">Both interest &amp; principal</option>
                    </select>
                    <p className="mt-1 text-[11px] leading-snug text-zinc-500">
                      {paymentAllocation === 'interest'
                        ? 'Reduce only pending interest. Principal stays the same.'
                        : paymentAllocation === 'principal'
                          ? `Pay outstanding principal only (₹${handPrincipalOut.toLocaleString('en-IN')} left — not the original loan amount). To close the file, use Full settlement.`
                          : 'Enter interest and principal amounts that add up to the total.'}
                    </p>
                  </div>
                ) : null}

                <AmountInput
                  id="collect-amount"
                  label={isOutPaymentMode ? 'Out payment received (₹)' : 'Amount received (₹)'}
                  value={amount}
                  disabled={formLocked || saving}
                  placeholder="0"
                  onChange={(next) => {
                    setAmount(next);
                    if (isHandLoan && paymentAllocation === 'both') {
                      const total = Math.max(0, parseAmountInput(next));
                      const i = Math.max(0, Math.round(parseAmountInput(interestAmount)));
                      if (i <= total) setPrincipalAmount(String(Math.max(0, total - i)));
                    }
                  }}
                />
                {isOutPaymentMode && outPaymentDue ? (
                  <p className="-mt-1 text-xs leading-snug text-zinc-500">
                    Due today: ₹{outPaymentDue.total.toLocaleString('en-IN')} (₹
                    {outPaymentDue.principal.toLocaleString('en-IN')} principal + ₹
                    {outPaymentDue.interest.toLocaleString('en-IN')} OP interest)
                  </p>
                ) : null}
                {!isHandLoan && paymentPreview && !payoff && !isOutPaymentMode && amtParsed > 0 ? (
                  <p className="-mt-1 text-xs leading-snug text-zinc-500">
                    {paymentPreview.afterOwed > 0
                      ? `₹${paymentPreview.afterOwed.toLocaleString('en-IN')} carries to next month`
                      : paymentPreview.afterOwed < 0
                        ? `₹${Math.abs(paymentPreview.afterOwed).toLocaleString('en-IN')} credit for next month`
                        : 'Cleared'}
                  </p>
                ) : null}

                {isHandLoan && !payoff && paymentAllocation === 'both' ? (
                  <div className="grid w-full grid-cols-1 gap-3">
                    <AmountInput
                      id="collect-interest-amt"
                      label="Interest amount (₹)"
                      value={interestAmount}
                      disabled={formLocked}
                      placeholder="0"
                      onChange={(next) => {
                        setInterestAmount(next);
                        const i = Math.max(0, Math.round(parseAmountInput(next)));
                        const total = Math.max(0, Math.round(parseAmountInput(amount)));
                        if (total > 0) setPrincipalAmount(String(Math.max(0, total - i)));
                      }}
                    />
                    <AmountInput
                      id="collect-principal-amt"
                      label="Principal amount (₹)"
                      value={principalAmount}
                      disabled={formLocked}
                      placeholder="0"
                      onChange={(next) => {
                        setPrincipalAmount(next);
                        const p = Math.max(0, Math.round(parseAmountInput(next)));
                        const total = Math.max(0, Math.round(parseAmountInput(amount)));
                        if (total > 0) setInterestAmount(String(Math.max(0, total - p)));
                      }}
                    />
                    <p className="text-[11px] leading-snug text-zinc-500">
                      Interest + Principal must equal total payment
                      {amtParsed > 0
                        ? ` (₹${Math.max(0, Math.round(parseAmountInput(interestAmount))).toLocaleString('en-IN')} + ₹${Math.max(0, Math.round(parseAmountInput(principalAmount))).toLocaleString('en-IN')} = ₹${amtParsed.toLocaleString('en-IN')})`
                        : ''}
                    </p>
                  </div>
                ) : null}

                {isHandLoan && handAfterPreview && !payoff && amtParsed > 0 ? (
                  <p className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs leading-snug text-zinc-600">
                    After payment → Principal ₹{handAfterPreview.principalOutstanding.toLocaleString('en-IN')}
                    {' · '}
                    Interest due ₹{handAfterPreview.interestDue.toLocaleString('en-IN')}
                  </p>
                ) : null}

                <div className="w-full min-w-0">
                  <div className="mb-1 flex flex-wrap items-end justify-between gap-2">
                    <span className="app-label mb-0">Overdue Charges received (₹)</span>
                    <p className="text-[11px] font-semibold tabular-nums text-rose-800">
                      Due ₹{lateDueNow.toLocaleString('en-IN')}
                      {calculatedLate.overdueDays > 0 ? (
                        <span className="font-medium text-rose-700/80">
                          {' '}
                          · {calculatedLate.overdueDays}d overdue
                        </span>
                      ) : null}
                      {lateParsed > 0 ? (
                        <>
                          {' · '}
                          Balance ₹
                          {Math.max(0, lateDueNow - lateParsed).toLocaleString('en-IN')}
                        </>
                      ) : null}
                    </p>
                  </div>
                  <AmountInput
                    id="collect-overdue-charges"
                    label=""
                    value={lateFee}
                    onChange={setLateFee}
                    placeholder={
                      clearingOverdueLate
                        ? lateDueNow > 0
                          ? `Enter ₹${lateDueNow.toLocaleString('en-IN')} overdue charges received`
                          : 'Enter overdue charges received'
                        : 'Optional overdue / penal charge'
                    }
                    disabled={formLocked}
                    hint={
                      clearingOverdueLate
                        ? lateDueNow > 0
                          ? `Enter overdue charges received to clear Due (e.g. ₹${lateDueNow.toLocaleString('en-IN')} for ${calculatedLate.overdueDays || '—'} overdue day(s)). Profile overdue days clear when this is fully paid. EMI goes in Amount received.`
                          : 'Enter overdue charges received to clear overdue days on the profile.'
                        : 'No overdue due right now. Use only if you need to record an extra penal charge.'
                    }
                  />
                </div>

                <div className="w-full min-w-0">
                  <PaymentViaFields
                    mode="collection"
                    value={paymentVia}
                    onChange={setPaymentVia}
                    disabled={formLocked}
                    totalAmount={
                      Math.max(0, parseAmountInput(amount)) + Math.max(0, parseAmountInput(lateFee))
                    }
                  />
                </div>

                <label className="flex w-full items-center gap-2 text-sm text-zinc-800">
                  <input
                    type="checkbox"
                    checked={payoff}
                    disabled={formLocked || saving || isOutPaymentMode}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setPayoff(on);
                      setConfirmSettlement(false);
                      if (on && isHandLoan) {
                        // Interest already paid → principal only; else principal + interest due.
                        const due =
                          handInterestDue > 0
                            ? Math.max(0, handPrincipalOut + handInterestDue + snap.accruedLateFees)
                            : Math.max(0, handPrincipalOut + snap.accruedLateFees);
                        if (due > 0) setAmount(String(due));
                      }
                    }}
                    className="h-4 w-4 shrink-0 rounded border-zinc-300"
                  />
                  Full settlement (close file)
                </label>
                {payoff ? (
                  <p className="w-full rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs leading-snug text-sky-950">
                    {isHandLoan ? (
                      <>
                        Contract due:{' '}
                        <span className="font-semibold tabular-nums">
                          ₹{(payoffBounds?.fullDue ?? handDueTotal + snap.accruedLateFees).toLocaleString('en-IN')}
                        </span>
                        . You can enter a lower amount for an owner discount (principal + late must still be covered).
                        {handInterestDue > 0 ? (
                          <>
                            {' '}
                            Includes interest ₹{handInterestDue.toLocaleString('en-IN')} — discount can waive part of
                            this.
                          </>
                        ) : null}
                      </>
                    ) : (
                      <>
                        Contract due:{' '}
                        <span className="font-semibold tabular-nums">
                          ₹{(payoffBounds?.fullDue ?? snap.settlementCashFloor).toLocaleString('en-IN')}
                        </span>
                        . You can enter a lower amount for an owner discount (e.g. ₹35,000 instead of ₹37,000). Principal
                        + late charges must still be covered — minimum{' '}
                        <span className="font-semibold tabular-nums">
                          ₹{(payoffBounds?.minimumDue ?? snap.settlementCashFloor).toLocaleString('en-IN')}
                        </span>
                        .
                        {settlementDiscountPreview > 0 ? (
                          <>
                            {' '}
                            Discount on this receipt:{' '}
                            <span className="font-semibold tabular-nums text-emerald-800">
                              ₹{settlementDiscountPreview.toLocaleString('en-IN')}
                            </span>
                            .
                          </>
                        ) : null}
                      </>
                    )}
                  </p>
                ) : null}

                <div className="w-full min-w-0">
                  <label className="app-label" htmlFor="collect-note">
                    Note
                  </label>
                  <input
                    id="collect-note"
                    type="text"
                    className="app-field box-border w-full min-w-0 max-w-full"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional"
                    disabled={formLocked}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <footer className="z-20 shrink-0 border-t border-zinc-200 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]">
          <div className="mx-auto flex w-full max-w-md gap-2">
            <button type="button" onClick={onClose} className="app-btn-secondary min-w-0 flex-1" disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              className="app-btn-primary inline-flex min-w-0 flex-1 items-center justify-center gap-2"
              disabled={formLocked || saving}
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                  Saving…
                </>
              ) : payoff ? (
                'Settle & save'
              ) : isOutPaymentMode ? (
                'Save out payment'
              ) : (
                'Save payment'
              )}
            </button>
          </div>
        </footer>
      </div>

      {confirmSettlement ? (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/45 p-4 sm:items-center">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="settle-confirm-title"
            className="w-full max-w-sm rounded-2xl border border-zinc-200 bg-white p-5 shadow-xl"
          >
            <p id="settle-confirm-title" className="text-base font-semibold text-zinc-900">
              Confirm full settlement?
            </p>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">
              This will close the remaining balance for <span className="font-semibold text-zinc-800">{facility.title}</span>
              {amtParsed > 0 ? (
                <>
                  {' '}
                  with payment of{' '}
                  <span className="font-semibold tabular-nums text-zinc-900">
                    ₹{amtParsed.toLocaleString('en-IN')}
                  </span>
                </>
              ) : null}
              . Contract due is{' '}
              <span className="font-semibold tabular-nums text-zinc-900">
                ₹{(payoffBounds?.fullDue ?? snap.settlementCashFloor).toLocaleString('en-IN')}
              </span>
              {settlementDiscountPreview > 0 ? (
                <>
                  {' '}
                  — owner discount{' '}
                  <span className="font-semibold tabular-nums text-emerald-800">
                    ₹{settlementDiscountPreview.toLocaleString('en-IN')}
                  </span>
                </>
              ) : null}
              . This cannot be undone from this screen.
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                className="app-btn-secondary min-w-0 flex-1"
                onClick={() => setConfirmSettlement(false)}
              >
                No, go back
              </button>
              <button type="button" className="app-btn-primary min-w-0 flex-1" onClick={submit} disabled={saving}>
                {saving ? 'Saving…' : 'Yes, settle'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </MobileModalPortal>
  );
}

function buildReceiptForInstallment(
  customer: UnifiedCustomer,
  row: CustomerInstallmentRow,
  payments: AdminEmiPayment[]
): PaymentReceiptInput {
  const profile = readCompanyProfile();
  const company = companyDisplayName(profile) || 'FINANCE';
  const fin = row.kind === 'finance' ? readFinanceRecords().find((r) => r.id === row.recordId) : null;
  const hand = row.kind === 'handloan' ? readHandLoanRecords().find((r) => r.id === row.recordId) : null;
  const data = (fin?.data || hand?.data || {}) as Record<string, unknown>;
  const vehicleNo = String(data.regNo || '').trim();
  const haNumber = String(data.haNumber || '').trim();
  const paidYmd = (row.paidAt || row.date || '').slice(0, 10);
  const matching = payments
    .filter((p) => p.recordId === row.recordId && String(p.paidAt || '').slice(0, 10) === paidYmd)
    .sort((a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime())[0];
  const fallbackPay = payments
    .filter((p) => p.recordId === row.recordId && Math.max(0, Number(p.amount) || 0) > 0)
    .sort((a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime())[0];
  const pay = matching || fallbackPay;
  const amount = Math.max(0, Math.round(Number(row.amount) || Number(pay?.amount) || 0));
  const mode =
    pay?.paymentVia === 'cash'
      ? 'Cash'
      : pay?.paymentVia === 'online'
        ? paymentViaDisplayLabel('online', pay.companyBankLabel)
        : pay?.paymentVia === 'split'
          ? 'Split (cash + bank)'
          : pay
            ? 'Cash / Bank'
            : 'Cash';
  const inst = row.installmentNo ? `(${row.installmentNo})` : 'EMI';
  const receiptNo =
    haNumber && row.installmentNo
      ? `${haNumber}/${row.installmentNo}`
      : pay?.id
        ? String(pay.id).slice(0, 8).toUpperCase()
        : `${String(row.recordId || '').slice(0, 6)}/${row.installmentNo || 1}`;

  return {
    receiptNo,
    receiptDate: paidYmd || defaultAdminEntryDate(),
    customerName: customer.name,
    vehicleNo,
    haNumber,
    amount,
    amountInWords: amountInIndianWords(amount),
    paymentMode: mode,
    paymentDated: paidYmd,
    towards: `Installment ${inst}`,
    companyName: company,
    companyPhone: profile.phone || profile.altPhone || '',
    companyAddress: companyComposedAddress(profile),
    partnerLabel: profile.partners?.[0]?.role || (profile.partners?.[0]?.name ? 'Mg. Partner' : 'Authorized Signatory'),
  };
}

function ScheduleTable({
  rows,
  customer,
  payments = [],
  emptyMessage,
}: {
  rows: CustomerInstallmentRow[];
  customer?: UnifiedCustomer;
  payments?: AdminEmiPayment[];
  emptyMessage?: string;
}) {
  const { t } = useTranslation();
  const [busyKey, setBusyKey] = useState<string | null>(null);

  if (rows.length === 0) {
    return <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-400">{emptyMessage ?? '—'}</p>;
  }

  const openReceipt = async (row: CustomerInstallmentRow, mode: 'print' | 'pdf') => {
    if (!customer) return;
    const key = `${row.recordId}-${row.date}-${row.installmentNo}-${mode}`;
    setBusyKey(key);
    try {
      const input = buildReceiptForInstallment(customer, row, payments);
      if (mode === 'print') printPaymentReceipt(input);
      else await downloadPaymentReceiptPdf(input);
    } catch {
      toast.error('Could not open payment receipt.');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
      <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-zinc-700">
        <thead className="bg-gray-50 dark:bg-zinc-800/90">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">{t('customer.due')}</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">{t('customer.facility')}</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">{t('customer.kind')}</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">{t('common.amount')}</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-zinc-400">{t('common.status')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-zinc-700">
          {rows.map((row, i) => {
            const showCumulative =
              row.status === 'Due' &&
              typeof row.cumulativeDue === 'number' &&
              row.cumulativeDue > row.amount;
            return (
              <tr key={`${row.date}-${row.recordId}-${i}`} className={row.status === 'Due' ? 'bg-amber-50/50 dark:bg-amber-950/25' : undefined}>
                <td className="whitespace-nowrap px-4 py-2 font-mono text-gray-800 dark:text-zinc-200">{row.date}</td>
                <td className="px-4 py-2 text-gray-900 dark:text-zinc-100">{row.facility}</td>
                <td className="px-4 py-2 text-gray-600 dark:text-zinc-300">{row.kind === 'finance' ? t('common.finance') : t('common.handLoans')}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right font-mono dark:text-zinc-200">
                  <div>₹{row.amount.toLocaleString('en-IN')}</div>
                  {showCumulative ? (
                    <div className="mt-0.5 text-[11px] font-semibold text-rose-700 dark:text-rose-300">
                      Total due ₹{Math.round(row.cumulativeDue || 0).toLocaleString('en-IN')}
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(row.status)}`}>
                      {translateStatus(row.status, t)}
                    </span>
                    {row.status === 'Paid' && customer ? (
                      <>
                        <button
                          type="button"
                          className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-900 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
                          disabled={busyKey != null}
                          onClick={() => void openReceipt(row, 'print')}
                        >
                          {busyKey?.endsWith('print') && busyKey.includes(String(row.installmentNo)) ? '…' : 'Receipt'}
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center rounded-md border border-zinc-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                          disabled={busyKey != null}
                          onClick={() => void openReceipt(row, 'pdf')}
                        >
                          PDF
                        </button>
                      </>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SeizeFacilityModal({
  facility,
  onClose,
}: {
  facility: UnifiedLoanFacility;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [seizedDate, setSeizedDate] = useState(() => defaultAdminEntryDate() || resolveWorkspaceAsOfDate());

  const submit = () => {
    const result = markFacilitySeized(facility.recordId, reason, seizedDate);
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    toast.success('Vehicle marked as seized.');
    onClose();
  };

  return (
    <MobileModalPortal onClose={onClose}>
      <div className="w-full max-w-lg rounded-t-2xl border border-gray-200 bg-white shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Mark vehicle as seized</h3>
            <p className="text-xs text-gray-500">{facility.title}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-500 hover:bg-gray-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 px-4 py-4">
          <p className="text-sm text-rose-900">
            Record why this vehicle is being seized. This is stored on the customer profile and shown in seized reports.
          </p>
          <div>
            <label className="app-label">Seizure date</label>
            <input type="date" className="app-field mt-1 app-field-date" value={seizedDate} onChange={(e) => setSeizedDate(e.target.value)} />
          </div>
          <div>
            <label className="app-label">Reason for seizure (required)</label>
            <textarea
              className="app-field mt-1 min-h-[6rem] resize-y"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Non-payment for 90+ days, vehicle recovered from borrower premises…"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] sm:pb-3">
          <button type="button" onClick={onClose} className="app-btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={submit} className="app-btn-primary border-rose-700 bg-rose-700 hover:border-rose-800 hover:bg-rose-800">
            Confirm seizure
          </button>
        </div>
      </div>
    </MobileModalPortal>
  );
}
